/** One-based physical lines; keep separators so untouched text round-trips exactly. */
export function numberDraftLines(draft) {
  const pieces = draft.split(/(\r\n|\n|\r)/);
  const lines = [];
  for (let i = 0; i < pieces.length; i += 2) {
    lines.push({ lineNumber: lines.length + 1, text: pieces[i], ending: pieces[i + 1] || "" });
  }
  return lines;
}

export function draftFromLines(lines) {
  if (!Array.isArray(lines) || !lines.length || lines.some((line, i) =>
    line.lineNumber !== i + 1 || typeof line.text !== "string" || /[\r\n]/.test(line.text) ||
    !["", "\n", "\r", "\r\n"].includes(line.ending) ||
    (i < lines.length - 1 ? !line.ending : line.ending !== ""))) {
    throw new Error("Invalid numbered draft lines");
  }
  return lines.map(line => line.text + line.ending).join("");
}

/** Validate the entire edit set against the original before applying any edit. */
export function applyDraftLineEdits(originalDraft, edits, { validatePunctuation = true } = {}) {
  return resolveDraftLineEdits(originalDraft, edits, { validatePunctuation }).editedDraft;
}

// Only reject comma-period sequences that cross a newly joined boundary.
// Existing punctuation elsewhere on the line must remain untouched.
// A dot followed by a digit starts a decimal, not a sentence-ending period.
const hasUnsafePunctuationJoin = (left, right) => /,[^\S\r\n]*$/.test(left) && /^[^\S\r\n]*\.(?!\d)/.test(right);

/** Optionally skip unlocatable targets; all other validation remains atomic. */
export function resolveDraftLineEdits(originalDraft, edits, { skipUnmatchedTargets = false, validatePunctuation = true } = {}) {
  if (!Array.isArray(edits) || edits.length > 100) throw new Error("Draft edit pass returned no edits array");
  const lines = numberDraftLines(originalDraft);
  const byLine = new Map();
  const appliedEdits = [];
  const skippedEdits = [];
  for (const edit of edits) {
    if (!edit || !Number.isInteger(edit.lineNumber) || edit.lineNumber < 1 || edit.lineNumber > lines.length ||
        typeof edit.textToReplace !== "string" || !edit.textToReplace || /[\r\n]/.test(edit.textToReplace) ||
        typeof edit.replacementText !== "string" || /[\r\n]/.test(edit.replacementText)) {
      throw new Error("Draft edit pass returned an invalid line edit");
    }
    const line = lines[edit.lineNumber - 1].text;
    if (/^# Scene \d+\s*$/.test(line)) throw new Error("Draft edit pass cannot edit scene headings");
    const start = line.indexOf(edit.textToReplace);
    if (start < 0 || line.indexOf(edit.textToReplace, start + 1) !== -1) {
      if (skipUnmatchedTargets) {
        skippedEdits.push({ ...edit, rejectionReason: start < 0 ? "Replacement target was not found on this line." : "Replacement target occurs more than once on this line." });
        continue;
      }
      throw new Error(`Draft edit pass line ${edit.lineNumber}: replacement target is missing or ambiguous`);
    }
    const changes = byLine.get(edit.lineNumber) || [];
    const end = start + edit.textToReplace.length;
    if (changes.some(other => start < other.end && end > other.start)) {
      throw new Error(`Draft edit pass line ${edit.lineNumber}: overlapping replacements`);
    }
    changes.push({ start, end, replacement: edit.replacementText });
    byLine.set(edit.lineNumber, changes);
    appliedEdits.push(edit);
  }
  for (const [lineNumber, changes] of byLine) {
    const line = lines[lineNumber - 1];
    for (const { start, end, replacement } of changes.sort((a, b) => b.start - a.start)) {
      const left = line.text.slice(0, start);
      const right = line.text.slice(end);
      if (validatePunctuation && (hasUnsafePunctuationJoin(left, replacement + right) ||
          (replacement.length > 0 && hasUnsafePunctuationJoin(left + replacement, right)))) {
        throw new Error(`Draft edit pass line ${lineNumber}: unsafe punctuation at replacement boundary`);
      }
      line.text = left + replacement + right;
    }
  }
  const editedDraft = draftFromLines(lines);
  const headings = text => [...text.matchAll(/^# Scene \d+[^\S\r\n]*\r?$/gm)].map(match => match[0]);
  if (!editedDraft.trim() || JSON.stringify(headings(originalDraft)) !== JSON.stringify(headings(editedDraft)) ||
      editedDraft.split(/^# Scene \d+[^\S\r\n]*\r?$/gm).slice(1).some(scene => !scene.trim())) {
    throw new Error("Draft edit pass returned an empty draft or changed the scene structure");
  }
  return { editedDraft, edits: appliedEdits, skippedEdits };
}
